'use client';
import { FormProvider, useForm } from 'react-hook-form';
import { Button } from '../../ui/button/button';
import { InfoIcon } from '../../ui/icons/icons';
import ImageWithFallback from '../../ui/image/image-with-fallback';
import Tooltip from '../../ui/tooltip/tooltip';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useEffect, useMemo, useRef, useState } from 'react';
import { getCalculationAction } from '../../vehicle-loan/actions';
import { ResponseModuleProductLoan } from '@/src/interfaces/data-contracts';
import { Pages } from '@/src/interfaces/pages-route';
import { useRouter } from 'next/navigation';
import InputField from '../../ui/react-hook-form/ui/input-field';
import fmt from '@/src/utils/helpers/number-format';
import { CurrencyTypeMissing } from '@/src/interfaces/missing-types';
import { ConditionalWrapper } from '@/src/utils/helpers/conditional-wrapper';
import { RelLink } from '@/src/features/ui/rel-link';

type Props = {
  product: ResponseModuleProductLoan;
  maturities: Pages.SmeHomeList.ResponseBody['maturities'];
  showDetails?: boolean;
  checkActiveRecourseForApply?: boolean;
  useResponseApplyLink?: boolean;
  typeId: number;
};

const getRecourseLink = (link: string, useResponseApplyLink?: boolean) => {
  if (useResponseApplyLink) return link;
  if (!(link.startsWith('/kredi/') && link.includes('/basvuru/'))) return link;
  if (link.split('/').at(-1)?.startsWith('yonlendirme'))
    return `/basvuru/ticari-tasit-kredisi/${link.split('/').at(-1)}`;
  if (link.split('/').at(-1)?.startsWith('form'))
    return `/ticari-tasit-kredisi/${link.split('/').at(3)}/basvuru/${link.split('/').at(-1)}`;
  return link;
};

const RecommendedLoanCard: React.FC<Props> = ({
  product,
  maturities,
  showDetails,
  checkActiveRecourseForApply,
  useResponseApplyLink,
  typeId,
}) => {
  const options = useMemo(() => {
    return (
      maturities?.options?.filter((option) => {
        if (!option.code) return false;
        const value = parseInt(option.code);
        const bracket = product.productActiveRate?.find(
          (bracket) =>
            bracket.maturityMin && bracket.maturityMax && value >= bracket.maturityMin && value <= bracket.maturityMax
        );
        if (bracket) return true;
        return false;
      }) ?? []
    );
  }, [product.productActiveRate, maturities]);

  const maxAmount = useMemo(() => {
    let result = 0;
    product.productActiveRate?.forEach((element) => {
      if (element?.maxAmount && element.maxAmount > result) result = element.maxAmount;
    });
    return result;
  }, [product.productActiveRate]);

  const minAmount = useMemo(() => {
    let result = 10000;
    product.productActiveRate?.forEach((element) => {
      if (element?.minAmount && element.minAmount < result) result = element.minAmount;
    });
    return result;
  }, [product.productActiveRate]);

  const schema = useMemo(() => {
    const schema = z
      .object({
        amount: z
          .string({ required_error: 'Tutar giriniz.' })
          .transform((value) => value.replace(/\./g, '').replace(',', '.'))
          .transform((value) => Number(value))
          .refine((val) => Number.isInteger(val), {
            message: 'Tutar küsuratsız olmalıdır.',
          })
          .refine((val) => val !== 0, { message: 'Tutar giriniz.' }),
        maturity: z.string(),
      })
      .superRefine(({ amount, maturity }, ctx) => {
        if (!maturity && amount && amount < minAmount) {
          ctx.addIssue({
            code: 'custom',
            message: `Tutar en az ${fmt(minAmount, CurrencyTypeMissing.TRY, true, false, 0)} olabilir.`,
            path: ['amount'],
          });
        }

        if (!maturity && amount && amount > maxAmount) {
          ctx.addIssue({
            code: 'custom',
            message: `Tutar en fazla ${fmt(maxAmount, CurrencyTypeMissing.TRY, true, false, 0)} olabilir.`,
            path: ['amount'],
          });
        }

        product.productActiveRate?.forEach(({ maturityMin, maturityMax, minAmount, maxAmount }) => {
          if (!maturity || !amount || !maturityMax || !maturityMin || !maxAmount || !minAmount) return;
          const maturityNum = parseInt(maturity);

          if (maturityNum >= maturityMin && maturityNum <= maturityMax && amount < minAmount) {
            ctx.addIssue({
              code: 'custom',
              message: `Tutar en az ${fmt(minAmount, CurrencyTypeMissing.TRY, true, false, 0)} olabilir.`,
              path: ['amount'],
            });
          }

          if (maturityNum >= maturityMin && maturityNum <= maturityMax && amount > maxAmount) {
            ctx.addIssue({
              code: 'custom',
              message: `Tutar en fazla ${fmt(maxAmount, CurrencyTypeMissing.TRY, true, false, 0)} olabilir.`,
              path: ['amount'],
            });
          }
        });
      });

    return schema;
  }, [options, product.productActiveRate, minAmount, maxAmount]);

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    mode: 'onChange',
  });

  const {
    handleSubmit,
    formState: { errors },
  } = form;

  const amount = form.watch('amount') as unknown as string;

  const bracketsForCurrentAmount = useMemo(() => {
    let amountNum: number | undefined;
    if (!amount) {
      amountNum = product.defaultAmount;
    } else {
      amountNum = Number(amount.replaceAll(/\./g, '').replace(',', '.'));
    }

    if (!amountNum) return;
    const result = product.productActiveRate?.filter(({ minAmount, maxAmount }) => {
      return maxAmount && amountNum <= maxAmount && minAmount && amountNum >= minAmount;
    });
    return result;
  }, [amount]);

  const router = useRouter();
  const [calculation, setCalculation] = useState<{
    interestRate: number;
    monthlyInstallment: number;
    totalAmount: number;
  } | null>();

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  const onSubmit = async (values: { amount: number; maturity: string }) => {
    const response = await getCalculationAction({ ...values, productId: product.productId ?? 0, typeId });

    if (response.isSuccess) {
      const { interestRate, monthlyInstallment, totalAmount } = response.data;
      setCalculation({ interestRate, monthlyInstallment, totalAmount });
    }
  };

  useEffect(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      handleSubmit(onSubmit)();
    }, 500);
    return () => {
      timeoutRef.current && clearTimeout(timeoutRef.current);
    };
  }, [amount]);

  const recourseLink = getRecourseLink(product.calculate?.recourseLink ?? '', useResponseApplyLink);

  return (
    <li
      key={product.productId}
      style={
        product.badge
          ? {
              backgroundImage:
                'linear-gradient(white, white), linear-gradient(180deg, #F59E0B 0%, rgba(245, 158, 11, 0) 45.83%)',
              backgroundOrigin: 'border-box',
              backgroundClip: 'padding-box, border-box',
              border: '1px solid transparent',
            }
          : { border: '1px solid white' }
      }
      className={`relative mr-4 w-[311px] min-w-[311px] overflow-hidden rounded-[12px] px-4 pb-4 pt-7 shadow-campCard last:mr-0 md:mr-8 md:w-[352px]`}
      data-testid="recommendedLoanItem"
    >
      {product.badge && (
        <span className="absolute right-0 top-0 flex h-[24px] w-[118px] items-center justify-center rounded-bl-[12px] bg-[#FEF7EB] px-4 py-1">
          <span className="text-xs font-bold -tracking-[0.3px] text-[#F6A927]" data-testid="badge">
            {product.badge}
          </span>
        </span>
      )}

      <div className="flex flex-col">
        <div className="mb-4 flex h-[50px] items-center">
          <div className="mr-4 flex h-[50px] w-[90px] shrink-0 items-center justify-center rounded-[8px] border-[1px] border-solid border-[#D8DADE]">
            <ConditionalWrapper
              condition={product.bank?.bankSeoUrl}
              wrapper={(children) => (
                <RelLink href={`${product.bank?.bankSeoUrl}`} data-testid="detailLink">
                  {children}
                </RelLink>
              )}
            >
              <ImageWithFallback
                width={75}
                height={50}
                src={product.bank?.logoPath}
                alt={product.bank?.name ?? 'bank name'}
                data-testid="bankLogo"
              />
            </ConditionalWrapper>
          </div>
          <div className="flex h-[40px] min-w-0 flex-col">
            <span className="mb-[2px] block text-[12px] font-bold leading-[18px] text-[#363940]" data-testid="bankName">
              <ConditionalWrapper
                condition={product.bank?.bankSeoUrl}
                wrapper={(children) => (
                  <RelLink href={`${product.bank?.bankSeoUrl}`} data-testid="detailLink">
                    {children}
                  </RelLink>
                )}
              >
                {product.bank?.name ?? 'Bank Name'}
              </ConditionalWrapper>
            </span>
            <span className="block truncate text-[12px] leading-[18px] text-[#848A97]" data-testid="productName">
              {product.calculate?.productName ?? 'Product Name'}
            </span>
          </div>
        </div>
        <FormProvider {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} data-testid="calculationForm">
            <div className="mb-6 flex h-[40px]">
              <div className="relative mr-3 w-[165px] md:w-[202px]">
                <span className="absolute right-3 top-1/2 z-10 -translate-y-1/2">TL</span>
                <InputField
                  overrideInputClassName="h-[40px] w-[165px] rounded-[8px] border border-solid border-[#D8DADE] bg-white px-3 py-2 md:w-[202px]"
                  defaultValue={
                    product.defaultAmount ? fmt(product.defaultAmount, CurrencyTypeMissing.TRY, false, false, 0) : ''
                  }
                  disableBuiltInErrorMessage
                  type="price"
                  name="amount"
                  label=""
                  maxValue={maxAmount}
                  data-testid="amount"
                />
                {errors.amount && (
                  <p className="mt-1 text-nowrap text-xs font-semibold text-error">{errors.amount?.message}</p>
                )}
              </div>
              <select
                {...form.register('maturity', {
                  onChange: () => {
                    form.trigger();
                    form.handleSubmit(onSubmit)();
                  },
                })}
                defaultValue={product.defaultMaturity}
                className="h-[40px] w-[100px] rounded-[8px] border border-solid border-[#D8DADE] bg-white px-3 py-2 text-[14px] font-semibold text-[#1D1E21] md:w-[106px]"
                data-testid="maturity"
              >
                {useMemo(() => {
                  return options.map((option) => {
                    if (!option.code) return <></>;
                    const valueNum = parseInt(option.code);
                    const isValidMaturityForCurrentAmount = bracketsForCurrentAmount?.some((bracket) => {
                      return (
                        bracket?.maturityMin &&
                        bracket.maturityMax &&
                        valueNum >= bracket.maturityMin &&
                        valueNum <= bracket.maturityMax
                      );
                    });
                    const optionText = isValidMaturityForCurrentAmount
                      ? option.name
                      : option.name
                          ?.split('')
                          .map((c) => c + '\u0336')
                          .join('');
                    return (
                      <option key={option.code} value={option.code} data-testid="maturityOption">
                        {optionText}
                      </option>
                    );
                  });
                }, [maturities?.options, product.productActiveRate, bracketsForCurrentAmount])}
              </select>
            </div>
            <p className="mb-2 text-[12px] text-[#848A97]">Girdiğiniz tutara göre vadeler değişiklik göstermektedir.</p>
            <div className="mb-2 flex h-[20px] justify-between text-[14px] leading-[20px] text-[#1D1E21]">
              <div className="flex items-center">
                <span className="mr-1 text-[12px] font-semibold text-[#848A97] md:text-[14px] md:font-normal">
                  Faiz Oranı
                </span>
                <Tooltip tooltipIcon={<InfoIcon />} position="left">
                  <span className="text-[12px] font-semibold">
                    Faiz oranı şirketinize göre değişiklik gösterebilir.
                  </span>
                </Tooltip>
              </div>
              <span className="font-semibold" data-testid="rate">
                %{calculation?.interestRate ?? product.calculate?.interestRate}
              </span>
            </div>
            <div className="mb-2 flex h-[20px] justify-between text-[14px] leading-[20px]">
              <span className="text-[12px] font-semibold text-[#848A97] md:text-[14px] md:font-normal">
                Aylık Taksit
              </span>
              <span className="text-[14px] font-semibold" data-testid="monthlyInstallment">
                {fmt(calculation?.monthlyInstallment, CurrencyTypeMissing.TRY, false, false, 0) ??
                  fmt(product.calculate?.monthlyInstallment, CurrencyTypeMissing.TRY, false, false, 0)}{' '}
                TL
              </span>
            </div>
            <div className="mb-7 flex h-[20px] justify-between text-[14px] leading-[20px]">
              <span className="text-[12px] font-semibold text-[#848A97] md:text-[14px] md:font-normal">
                Toplam Ödeme
              </span>
              <span className="text-[14px] font-semibold" data-testid="totalAmount">
                {fmt(calculation?.totalAmount, CurrencyTypeMissing.TRY, false, false, 0) ??
                  fmt(product.calculate?.totalAmount, CurrencyTypeMissing.TRY, false, false, 0)}{' '}
                TL
              </span>
            </div>
          </form>
        </FormProvider>
        <div className="flex gap-[12px]">
          {showDetails && product.calculate?.detailUrl && (
            <Button
              onClick={() => router.push(product.calculate?.detailUrl ?? '')}
              text="İncele"
              type="button"
              className="h-[48px] w-full flex-1 basis-[106px] rounded-lg text-[14px] font-bold"
              data-testid="detailsButton"
              isNegative
            />
          )}
          {(!checkActiveRecourseForApply || product.calculate?.isActiveRecourse) && (
            <Button
              onClick={() => {
                const amount = String(form.getValues().amount).replace(/\D/g, '');
                const maturity = form.getValues().maturity;

                router.push(
                  recourseLink.replace(/amount=\d+/, `amount=${amount}`).replace(/maturity=\d+/, `maturity=${maturity}`)
                );
              }}
              disabled={!form.formState.isValid}
              text="Hemen Başvur"
              type="button"
              className="h-[48px] w-full flex-1 basis-[202px] rounded-lg bg-[#4A49BB] text-[14px] font-bold"
              data-testid="applyButton"
            />
          )}
        </div>
      </div>
    </li>
  );
};

export default RecommendedLoanCard;
